import { View, Platform, Image, Text, ImageSourcePropType } from 'react-native';
import { useTheme } from '@/theme';

export type TripFlowLogoProps = {
  size?: number;
  wordmarkVariant?: 'default' | 'eyebrow';
  /** When true, use page background for the icon container so it blends (no visible box on dark screens). */
  blendWithBackground?: boolean;
};

// UI logo mark (squircle icon on transparent canvas) — not the raw app-icon PNG,
// which has a full-bleed opaque background and breaks when embedded in screens.
const logoMark: ImageSourcePropType = require('../../../assets/logo-mark.png');

export function TripFlowLogo({
  size = 164,
  wordmarkVariant = 'default',
  blendWithBackground = false,
}: TripFlowLogoProps) {
  const theme = useTheme();

  const iconSize = size * 0.62;
  const labelSize =
    wordmarkVariant === 'eyebrow'
      ? Math.min(20, Math.max(18, Math.round(size * 0.14)))
      : Math.max(22, Math.round(size * 0.22));

  return (
    <View
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.25),
      }}
    >
      <View
        style={{
          width: iconSize,
          height: iconSize,
          alignItems: 'center',
          justifyContent: 'center',
          ...(blendWithBackground
            ? {}
            : Platform.OS === 'web'
              ? {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                }
              : {
                  shadowColor: theme.colors.overlay,
                  shadowOpacity: 0.18,
                  shadowOffset: { width: 0, height: 2 },
                  shadowRadius: 8,
                }),
        }}
      >
        <Image
          source={logoMark}
          style={{
            width: iconSize,
            height: iconSize,
            resizeMode: 'contain',
          }}
          accessibilityLabel="Tripvio app icon"
          onError={(error) => {
            console.error('Failed to load Tripvio logo mark:', error);
          }}
        />
      </View>

      <Text
        accessibilityLabel="tripvio"
        style={{
          color: theme.colors.text,
          fontSize: labelSize,
          fontWeight: wordmarkVariant === 'eyebrow' ? '600' : '700',
          letterSpacing: wordmarkVariant === 'eyebrow' ? 1.1 : -0.3,
          opacity: wordmarkVariant === 'eyebrow' ? 0.9 : 1,
        }}
      >
        tripvio
      </Text>
    </View>
  );
}
